/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { InfoCircleOutlined, UserAddOutlined } from '@ant-design/icons'
import React, { useCallback, useState } from 'react'
import { connect } from 'react-redux'
import colors from '@sportninja/common/constants/appColors'
import { getSoccerSport } from '@sportninja/common/selectors/types'
import { enablePlayerTeamFinder } from '@sportninja/common/utils/utils'
import HeaderActionButton from 'src/components/ActionButton/HeaderActionButton'
import FullScreenOverlay, {
  FullScreenHeader,
} from 'src/components/FullScreenOverlay'
import HeaderCancelIcon from 'src/features/profile/player/setupModal/components/HeaderCancelIcon'
import ManageTeamAttendanceModalContent from './ManageTeamAttendanceModalContent'
import AttendanceHelpModal from './components/AttendanceHelpModal'
import PlayerFinderModal from 'src/features/playerFinder/PlayerFinderModal'

type ManageTeamAttendanceModalProps = {
  isManageAttendanceModalOpen: boolean
  setIsManageAttendanceModalOpen: (isOpen: boolean) => void
  selectedManageAttendance: {
    gameId: string
    rosterId: string
    teamId: string
    teamName: string
    showWaiver: boolean
    // Viewer isn't a team official on this team: view the attendance only…
    isReadOnly: boolean
    // …except the availability of their own players on it.
    ownPlayerIds: string[]
  } | null
  readAttendanceInformation: () => void
  soccerSportId?: number
}

/**
 * Game Attendance — a full-screen page (same shell as Edit Player): BACK link,
 * title and the header actions; the game card, counts and attendance sections
 * fill the rest.
 */
const ManageTeamAttendanceModal: React.FC<ManageTeamAttendanceModalProps> = ({
  isManageAttendanceModalOpen,
  setIsManageAttendanceModalOpen,
  selectedManageAttendance,
  readAttendanceInformation,
  soccerSportId,
}) => {
  const [isHelpOpen, setIsHelpOpen] = useState(false)
  const [isFinderOpen, setIsFinderOpen] = useState(false)
  // SN-8453 — Player Finder is Canlan-only, and adding players is an edit.
  const canOpenPlayerFinder =
    enablePlayerTeamFinder && !selectedManageAttendance?.isReadOnly

  const handleClose = useCallback(() => {
    readAttendanceInformation()
    setIsManageAttendanceModalOpen(false)
  }, [readAttendanceInformation, setIsManageAttendanceModalOpen])

  return (
    <>
      <FullScreenOverlay
        isOpen={isManageAttendanceModalOpen && !!selectedManageAttendance}
      >
        {selectedManageAttendance ? (
          <>
            <FullScreenHeader
              title={`Game Attendance - ${selectedManageAttendance.teamName}`}
              onBack={handleClose}
              titleAdornment={
                <InfoCircleOutlined
                  css={infoIconStyle}
                  onClick={() => setIsHelpOpen(true)}
                  title='Attendance status help'
                  aria-label='Attendance status help'
                />
              }
              actions={
                <>
                  {canOpenPlayerFinder ? (
                    <HeaderActionButton
                      text='Add Player with Player Finder'
                      onClick={() => setIsFinderOpen(true)}
                      variant='primary'
                      textWeight='bold'
                      icon={<UserAddOutlined />}
                    />
                  ) : null}
                  {/* Close sits at the far right. */}
                  <HeaderActionButton
                    text='Close'
                    onClick={handleClose}
                    variant='secondary'
                    textWeight='medium'
                    icon={<HeaderCancelIcon />}
                  />
                </>
              }
            />
            <ManageTeamAttendanceModalContent
              gameId={selectedManageAttendance.gameId}
              rosterId={selectedManageAttendance.rosterId}
              showWaiver={selectedManageAttendance.showWaiver}
              isReadOnly={selectedManageAttendance.isReadOnly}
              ownPlayerIds={selectedManageAttendance.ownPlayerIds}
              soccerSportId={soccerSportId}
            />
          </>
        ) : null}
      </FullScreenOverlay>
      <AttendanceHelpModal
        open={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
      />
      {canOpenPlayerFinder && selectedManageAttendance ? (
        <PlayerFinderModal
          isOpen={isFinderOpen}
          onClose={() => setIsFinderOpen(false)}
          gameId={selectedManageAttendance.gameId}
          rosterId={selectedManageAttendance.rosterId}
          teamId={selectedManageAttendance.teamId}
        />
      ) : null}
    </>
  )
}

const mapStateToProps = (state: any) => ({
  soccerSportId: getSoccerSport(state)?.id,
})

export default connect(mapStateToProps)(ManageTeamAttendanceModal)

// ---- styled ----
const infoIconStyle = css`
  color: ${colors.PRIMARY};
  font-size: 24px;
  cursor: pointer;
  transition: opacity 0.2s;
  &:hover {
    opacity: 0.8;
  }
`
